// ui/src/brand-marks.ts
// The actual outlines of the three third-party marks, drawn in one colour.
//
// Not the full-colour logos. Slack's mark is four colours and Stripe's and HubSpot's
// are their own, and dropping three foreign palettes into a page with one accent
// would fight everything else on it. What is worth having is the *shape*: the Stripe
// S, the HubSpot sprocket, the Slack hash are recognised long before the label is
// read. So the published outlines are used, filled with the tile's own tint.
//
// Inlined rather than fetched, for the same reason the fonts are self-hosted: no
// visitor's IP reaches anyone else's server when this page loads.
//
// Provenance, because using someone's mark without saying where it came from is how
// this goes wrong:
//   Stripe, HubSpot  simple-icons (CC0-1.0), 24x24 viewBox
//   Slack            devicon (MIT), 128x128 viewBox, its four paths merged to one
//                    colour. Slack had its mark removed from simple-icons at its own
//                    request, so if that ever matters here, this is the line to cut.
//
// Used to identify the systems this demo delivers to, which is what marks are for.
// Our own invoice service and mailer have no mark to be faithful to and keep a
// function glyph instead.

export interface BrandMark {
  viewBox: string;
  paths: string[];
}

export const BRAND_MARKS: Record<string, BrandMark> = {
  stripe: {
    viewBox: '0 0 24 24',
    paths: ["M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.594-7.305h.003z"],
  },
  hubspot: {
    viewBox: '0 0 24 24',
    paths: ["M18.164 7.93V5.084a2.198 2.198 0 001.267-1.978v-.067A2.2 2.2 0 0017.238.845h-.067a2.2 2.2 0 00-2.193 2.193v.067a2.196 2.196 0 001.252 1.973l.013.006v2.852a6.22 6.22 0 00-2.969 1.31l.012-.01-7.828-6.095A2.497 2.497 0 104.3 4.656l-.012.006 7.697 5.991a6.176 6.176 0 00-1.038 3.446c0 1.343.425 2.588 1.147 3.607l-.013-.02-2.342 2.343a1.968 1.968 0 00-.58-.095h-.002a2.033 2.033 0 102.033 2.033 1.978 1.978 0 00-.1-.595l.005.014 2.317-2.317a6.247 6.247 0 104.782-11.134l-.036-.005zm-.964 9.378a3.206 3.206 0 113.215-3.207v.002a3.206 3.206 0 01-3.207 3.207z"],
  },
  slack: {
    viewBox: '0 0 128 128',
    paths: [
      "M27.255 80.719c0 7.33-5.978 13.317-13.309 13.317C6.616 94.036.63 88.049.63 80.719s5.987-13.317 13.317-13.317h13.309zm6.709 0c0-7.33 5.987-13.317 13.317-13.317s13.317 5.986 13.317 13.317v33.335c0 7.33-5.986 13.317-13.317 13.317-7.33 0-13.317-5.987-13.317-13.317zm0 0",
      "M47.281 27.255c-7.33 0-13.317-5.978-13.317-13.309C33.964 6.616 39.951.63 47.281.63s13.317 5.987 13.317 13.317v13.309zm0 6.709c7.33 0 13.317 5.987 13.317 13.317s-5.986 13.317-13.317 13.317H13.946C6.616 60.598.63 54.612.63 47.281c0-7.33 5.987-13.317 13.317-13.317zm0 0",
      "M100.745 47.281c0-7.33 5.978-13.317 13.309-13.317 7.33 0 13.317 5.987 13.317 13.317s-5.987 13.317-13.317 13.317h-13.309zm-6.709 0c0 7.33-5.987 13.317-13.317 13.317s-13.317-5.986-13.317-13.317V13.946C67.402 6.616 73.388.63 80.719.63c7.33 0 13.317 5.987 13.317 13.317zm0 0",
      "M80.719 100.745c7.33 0 13.317 5.978 13.317 13.309 0 7.33-5.987 13.317-13.317 13.317s-13.317-5.987-13.317-13.317v-13.309zm0-6.709c-7.33 0-13.317-5.987-13.317-13.317s5.986-13.317 13.317-13.317h33.335c7.33 0 13.317 5.986 13.317 13.317 0 7.33-5.987 13.317-13.317 13.317zm0 0",
    ],
  },
};
